import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, EmptyState } from "../../components/ui";
import { Icon } from "../shared/components";
import { OnboardingTrigger } from "../onboarding/OnboardingFlow";
import NewConversationModal from "../whatsapp/NewConversationModal";
import {
  API, ensureArray, ensureObject, formatTime,
  formatMinutesAsLabel, getSlaMeta,
  getInitials, formatBrazilPhone,
} from "../shared/utils";

/* ---------- Metric (v2) ---------- */
function Metric({ label, value, unit, sub, icon, accent, trend }) {
  const accentStyle =
    accent === "brand" ? { background: "rgba(37,211,102,0.10)", borderColor: "rgba(37,211,102,0.25)", color: "var(--brand-500)" } :
    accent === "danger" ? { background: "var(--danger-bg)", borderColor: "var(--danger-border)", color: "var(--danger)" } :
    accent === "success" ? { background: "var(--success-bg)", borderColor: "var(--success-border)", color: "var(--success)" } :
    accent === "info" ? { background: "var(--info-bg)", borderColor: "var(--info-border)", color: "var(--info)" } :
    accent === "warning" ? { background: "var(--warning-bg)", borderColor: "var(--warning-border)", color: "var(--warning)" } :
    accent === "purple" ? { background: "var(--purple-bg)", borderColor: "rgba(167,139,250,0.25)", color: "var(--purple)" } :
    {};
  return (
    <div className="metric">
      <div className="metric-label">
        <span className="metric-icon" style={accentStyle}><Icon name={icon} size={11} /></span>
        {label}
      </div>
      <div className="metric-value">
        {value}
        {unit && <span className="metric-value-unit">{unit}</span>}
      </div>
      {trend ? (
        <div className={`metric-trend metric-trend-${trend.dir}`}>
          <Icon name={trend.dir === "up" ? "trendUp" : "trendDown"} size={11} />
          {trend.val}
          {sub && <span style={{ color: "var(--text-4)", fontWeight: 400, marginLeft: 4 }}>· {sub}</span>}
        </div>
      ) : (
        sub && <div className="metric-sub">{sub}</div>
      )}
    </div>
  );
}

/* ---------- Live agents card ---------- */
function LiveAgentsCard({ users, conversations, onGotoChat, avgFirstResponse }) {
  const open = useMemo(() => conversations.filter((c) => c.status !== "finalizado"), [conversations]);
  const activeUsers = useMemo(() => users.filter((u) => u.active === 1 && u.role !== "master"), [users]);

  const stats = useMemo(() => {
    return activeUsers.map((u) => {
      const userConvs = open.filter((c) => Number(c.assigned_to) === Number(u.id));
      const hasCritical = userConvs.some((c) => getSlaMeta(c).tone === "critical");
      const lastMin = userConvs.length
        ? Math.min(...userConvs.map((c) => {
            const t = c.updated_at || c.created_at;
            return t ? Math.floor((Date.now() - new Date(t).getTime()) / 60000) : Infinity;
          }))
        : null;
      const presence = lastMin == null ? "offline" : lastMin <= 5 ? "online" : lastMin <= 30 ? "away" : "offline";
      return { user: u, activeChats: userConvs.length, total: userConvs.length, hasCritical, presence };
    });
  }, [activeUsers, open]);

  const respLabel = avgFirstResponse ? formatMinutesAsLabel(avgFirstResponse) : null;

  return (
    <div className="card-v2">
      <div className="card-header">
        <div>
          <div className="card-title">Ao vivo · {activeUsers.length} atendentes</div>
          <div className="card-subtitle">Acompanhe em tempo real quem está atendendo cada cliente</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className="badge badge-success"><span className="badge-dot" />Tempo real</span>
          <button className="btn btn-secondary btn-sm" onClick={onGotoChat}>
            <Icon name="chat" size={12} /> Ir para Conversas
          </button>
        </div>
      </div>
      <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {stats.length === 0 ? (
          <div style={{ gridColumn: "1 / -1", padding: 32, textAlign: "center", color: "var(--text-3)" }}>
            <Icon name="people" size={28} />
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)", fontWeight: 600 }}>Nenhum atendente ativo</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Cadastre atendentes em Configurações.</div>
          </div>
        ) : stats.slice(0, 6).map(({ user, activeChats, total, hasCritical, presence }) => {
          // Cor determinística do avatar (mesmo algoritmo do Avatar component)
          const AV_COLORS = ['#25D366','#3B9DFF','#A78BFA','#F471B5','#F5A524','#34D2C8','#FF7849'];
          const seed = [...String(user.name || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
          const avatarColor = AV_COLORS[seed % AV_COLORS.length];
          return (
          <div key={user.id}
            className={`live-agent-card${hasCritical ? " live-agent-card-critical" : ""}`}
            style={{ background: `linear-gradient(135deg, ${avatarColor}12 0%, ${avatarColor}05 40%, var(--bg-1) 70%)`, borderColor: `${avatarColor}22` }}
          >
            <div className="live-agent-row">
              <Avatar name={user.name} size="md" src={user.avatar_url} status={presence} />
              <div className="live-agent-info">
                <div className="live-agent-name">{user.name}</div>
                <div className="live-agent-email">{user.email || user.role}</div>
              </div>
              <span className="badge badge-neutral">{activeChats} ativos</span>
            </div>
            <div className="live-agent-stats">
              <span><strong>{total}</strong> total</span>
              {respLabel && total > 0 && (
                <>
                  <span className="live-agent-stats-sep">·</span>
                  <span>resp. <strong>{respLabel}</strong></span>
                </>
              )}
              {hasCritical && (
                <>
                  <span className="live-agent-stats-sep">·</span>
                  <span style={{ color: "var(--danger)" }}>SLA crítico</span>
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Activity feed card ---------- */
function ActivityCard({ conversations, onGotoChat }) {
  const items = useMemo(() => {
    const recent = [...conversations]
      .filter((c) => c.updated_at)
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 6);
    return recent.map((c) => {
      const sla = getSlaMeta(c);
      const unread = Number(c.unread_count || 0);
      let icon = "message", color = "info", title, sub;
      if (c.status === "finalizado") {
        icon = "checkCircle"; color = "success";
        title = `Conversa #${c.id} finalizada`;
        sub = `${c.contact_name || "Contato"} · ${formatTime(c.updated_at)}`;
      } else if (sla.tone === "critical") {
        icon = "warning"; color = "warning";
        title = `SLA crítico em #${c.id}`;
        sub = `${c.contact_name || "Contato"} · ${formatTime(c.updated_at)}`;
      } else if (!c.assigned_to) {
        icon = "message"; color = "info";
        title = `Nova conversa de ${c.contact_name || "contato"}`;
        sub = `Pendente atribuição · ${formatTime(c.updated_at)}`;
      } else if (unread > 0) {
        icon = "message"; color = "info";
        title = `${unread} mensagem(ns) de ${c.contact_name || "contato"}`;
        sub = `${c.attendant_name || "Atendente"} · ${formatTime(c.updated_at)}`;
      } else {
        icon = "message"; color = "brand";
        title = `Conversa #${c.id} atualizada`;
        sub = `${c.attendant_name || "—"} · ${formatTime(c.updated_at)}`;
      }
      return { icon, color, title, sub, key: c.id };
    });
  }, [conversations]);

  const accentStyle = (color) => (
    color === "success" ? { background: "var(--success-bg)", borderColor: "var(--success-border)", color: "var(--success)", width: 26, height: 26 } :
    color === "warning" ? { background: "var(--warning-bg)", borderColor: "var(--warning-border)", color: "var(--warning)", width: 26, height: 26 } :
    color === "brand" ? { background: "rgba(37,211,102,0.10)", borderColor: "rgba(37,211,102,0.25)", color: "var(--brand-500)", width: 26, height: 26 } :
    { background: "var(--info-bg)", borderColor: "var(--info-border)", color: "var(--info)", width: 26, height: 26 }
  );

  return (
    <div className="card-v2">
      <div className="card-header">
        <div className="card-title">Atividade recente</div>
        <button className="btn btn-ghost btn-sm" onClick={onGotoChat}>Ver tudo</button>
      </div>
      <div style={{ padding: "8px 12px 12px" }}>
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-4)", fontSize: 12 }}>
            Sem atividade recente
          </div>
        ) : items.map((it) => (
          <div className="activity-row" key={it.key}>
            <span className="metric-icon" style={accentStyle(it.color)}>
              <Icon name={it.icon} size={12} />
            </span>
            <div className="activity-text">
              <div className="activity-title">{it.title}</div>
              <div className="activity-sub">{it.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Live queue columns ---------- */
function QueueColumn({ title, count, items, accent, users, onPreview }) {
  return (
    <div className={`queue-col${accent === "danger" ? " queue-col-danger" : ""}`}>
      <div className="queue-col-head">
        <div className="queue-col-head-left">
          <span className={`queue-col-dot${accent === "danger" ? " queue-col-dot-danger" : ""}`} />
          <span className="queue-col-title">{title}</span>
          <span className={`count${accent === "danger" ? " count-danger" : ""}`}>{count}</span>
        </div>
        <button className="tb-icon-btn" style={{ width: 24, height: 24 }} title="Opções">
          <Icon name="moreV" size={13} />
        </button>
      </div>
      <div className="queue-col-list">
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-4)", fontSize: 12 }}>
            Sem conversas ativas
          </div>
        ) : items.map((c) => {
          const lastMin = (() => {
            const t = c.updated_at || c.created_at;
            if (!t) return Infinity;
            return Math.floor((Date.now() - new Date(t).getTime()) / 60000);
          })();
          const presence = lastMin <= 5 ? "online" : lastMin <= 30 ? "away" : "offline";
          return (
            <button key={c.id} type="button" className="queue-row" onClick={() => onPreview(c)}>
              <div className="queue-row-top">
                <Avatar name={c.contact_name} size="sm" src={c.contact_avatar_url} status={presence} />
                <div className="queue-row-info">
                  <div className="queue-row-name">{c.contact_name || "Sem nome"}</div>
                  <div className="queue-row-time">{formatTime(c.updated_at || c.created_at)}</div>
                </div>
                {Number(c.unread_count || 0) > 0 && (
                  <span className="count count-brand">{c.unread_count}</span>
                )}
              </div>
              <div className="queue-row-preview">{c.last_message || "Sem mensagens"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LiveQueues({ conversations, users, onPreview, onGotoChat }) {
  const open = useMemo(() => conversations.filter((c) => c.status !== "finalizado"), [conversations]);
  const pending = useMemo(() => open.filter((c) => !c.assigned_to), [open]);
  const activeUsers = useMemo(
    () => users.filter((u) => u.active === 1 && u.role !== "master").slice(0, 3),
    [users]
  );

  const cols = [
    { title: "Pendentes", accent: "danger", items: pending.slice(0, 5), count: pending.length },
    ...activeUsers.map((u) => {
      const userConvs = open.filter((c) => Number(c.assigned_to) === Number(u.id));
      return { title: u.name, accent: "neutral", items: userConvs.slice(0, 5), count: userConvs.length };
    }),
  ];

  while (cols.length < 4) cols.push({ title: "—", accent: "neutral", items: [], count: 0 });

  return (
    <div className="page-section-v2" style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="section-title-v2">
          Filas ao vivo
          <span className="badge badge-brand">{open.length} conversas</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onGotoChat}>
          <Icon name="chat" size={12} /> Ir para Conversas
          <Icon name="arrowRight" size={12} />
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {cols.map((col, i) => (
          <QueueColumn key={i} {...col} users={users} onPreview={onPreview} />
        ))}
      </div>
    </div>
  );
}

/* ---------- Live preview modal ---------- */
function LivePreviewModal({ api, conversation, onClose, onGotoChat }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/conversations/${conversation.id}/messages`)
      .then((res) => { if (!cancelled) setMessages(ensureArray(res.data).slice(-30)); })
      .catch(() => { if (!cancelled) setMessages([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, conversation.id]);

  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-v2 modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Avatar name={conversation.contact_name} size="md" src={conversation.contact_avatar_url} />
            <div>
              <div className="modal-title">{conversation.contact_name || "Conversa"}</div>
              <div className="modal-subtitle">
                {formatBrazilPhone(conversation.contact_phone || "") || conversation.contact_phone || "—"}
                {conversation.attendant_name && ` · Atendente: ${conversation.attendant_name}`}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={14} />
          </button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 360 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "var(--text-3)", padding: 32 }}>Carregando mensagens…</div>
          ) : messages.length === 0 ? (
            <EmptyState illustration="chat" title="Sem mensagens" text="Esta conversa ainda não tem mensagens." />
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`livePreviewBubble ${m.sender_type === "atendente" ? "outgoing" : "incoming"}`}>
                <p>{m.message || "[mídia]"}</p>
                <small>{formatTime(m.created_at)}</small>
              </div>
            ))
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-primary" onClick={onGotoChat}>
            <Icon name="arrowRight" size={14} /> Ir para conversa
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */
function ExecutiveDashboard({ api, user, setActivePage }) {
  const [summary, setSummary] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [sessionStatus, setSessionStatus] = useState({ status: "disconnected" });
  const [previewConv, setPreviewConv] = useState(null);
  const [period, setPeriod] = useState("hoje");
  const [refreshing, setRefreshing] = useState(false);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const streamRef = useRef(null);
  const reconnectRef = useRef(null);

  async function loadData() {
    try {
      setRefreshing(true);
      const [summaryRes, convRes, usersRes, sessionRes] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/conversations", { params: { limit: 200 } }),
        api.get("/users"),
        api.get("/whatsapp/session"),
      ]);
      setSummary(ensureObject(summaryRes.data));
      // API retorna objeto paginado { conversations: [...], has_more, next_cursor }
      const convData = convRes.data;
      const convList = Array.isArray(convData) ? convData : ensureArray(convData.conversations);
      setConversations(convList);
      setUsers(ensureArray(usersRes.data));
      setSessionStatus(ensureObject(sessionRes.data) || { status: "disconnected" });
    } catch {} finally {
      setTimeout(() => setRefreshing(false), 600);
    }
  }

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  // SSE
  useEffect(() => {
    let cancelled = false;
    async function connectSSE() {
      try {
        const tokenRes = await api.post("/realtime/token");
        if (cancelled) return;
        const source = new EventSource(`${API}/realtime/stream?token=${encodeURIComponent(tokenRes.data.token)}`);
        streamRef.current = source;
        const refresh = () => { if (!cancelled) loadData(); };
        source.addEventListener("message_created", refresh);
        source.addEventListener("conversation_updated", refresh);
        source.addEventListener("summary_updated", refresh);
        source.addEventListener("session_updated", refresh);
        source.onerror = () => {
          source.close();
          if (!cancelled) reconnectRef.current = setTimeout(connectSSE, 3000);
        };
      } catch {
        if (!cancelled) reconnectRef.current = setTimeout(connectSSE, 3000);
      }
    }
    connectSSE();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (streamRef.current) streamRef.current.close();
    };
    // eslint-disable-next-line
  }, [api]);

  const isConnected = sessionStatus.status === "connected";

  const openCount = summary?.open_conversations ?? 0;
  const waitingCount = summary?.waiting_conversations ?? 0;
  const criticalCount = summary?.critical_conversations ?? 0;
  const warningCount = summary?.warning_conversations ?? 0;

  const metrics = [
    {
      label: "Conversas abertas", value: openCount, icon: "chat", accent: "info",
      trend: { dir: openCount > 0 ? "up" : "down", val: `${waitingCount} aguardando` },
      sub: "atenção necessária",
    },
    {
      label: "Tempo médio 1ª resp.", value: formatMinutesAsLabel(summary?.avg_first_response_minutes ?? 0),
      icon: "clock", accent: "success",
      sub: "tempo operacional",
    },
    {
      label: "Leads ativos", value: summary?.total_leads ?? 0, icon: "deal", accent: "brand",
      trend: { dir: "up", val: "no funil" },
    },
    {
      label: "SLA crítico", value: criticalCount, icon: "warning", accent: "danger",
      trend: criticalCount > 0 ? { dir: "up", val: `+${criticalCount} crítico(s)` } : undefined,
      sub: criticalCount === 0 ? `${warningCount} em atenção` : undefined,
    },
    {
      label: "WhatsApp", value: isConnected ? "Conectado" : "Off", icon: "whatsapp",
      accent: isConnected ? "brand" : "warning",
      sub: isConnected ? (sessionStatus.phone || "sessão ativa") : "conectar sessão",
    },
  ];

  return (
    <div className="page-v2">
      <div className="page-header-v2">
        <div>
          <div className="page-title-v2">Resumo</div>
          <div className="page-subtitle-v2">
            Visão geral em tempo real da operação · atualizado agora
          </div>
        </div>
        <div className="page-actions-v2">
          <div className="segment">
            {[{ value: "hoje", label: "Hoje" }, { value: "semana", label: "7 dias" }, { value: "mes", label: "30 dias" }].map((opt) => (
              <button
                key={opt.value}
                className={`segment-item${period === opt.value ? " active" : ""}`}
                onClick={() => setPeriod(opt.value)}
                type="button"
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={loadData} disabled={refreshing}>
            <Icon name="refresh" size={13} /> {refreshing ? "Atualizando…" : "Atualizar"}
          </button>
          <button className="btn btn-primary" onClick={() => setNewConvOpen(true)}>
            <Icon name="plus" size={13} /> Nova conversa
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
        {metrics.map((m, i) => <Metric key={i} {...m} />)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <LiveAgentsCard
          users={users}
          conversations={conversations}
          onGotoChat={() => setActivePage("atendimentos")}
          avgFirstResponse={summary?.avg_first_response_minutes}
        />
        <ActivityCard conversations={conversations} onGotoChat={() => setActivePage("atendimentos")} />
      </div>

      <LiveQueues
        conversations={conversations}
        users={users}
        onPreview={setPreviewConv}
        onGotoChat={() => setActivePage("atendimentos")}
      />

      {previewConv && (
        <LivePreviewModal
          api={api}
          conversation={previewConv}
          onClose={() => setPreviewConv(null)}
          onGotoChat={() => {
            const id = previewConv.id;
            setPreviewConv(null);
            setActivePage("atendimentos", { conversationId: id });
          }}
        />
      )}

      <div className="dashboardOnboardingRow">
        <OnboardingTrigger />
      </div>

      {newConvOpen && (
        <NewConversationModal
          api={api}
          user={user}
          users={users}
          onClose={() => setNewConvOpen(false)}
          onSuccess={(conv) => {
            setNewConvOpen(false);
            setActivePage("atendimentos", { conversationId: conv.id });
          }}
        />
      )}
    </div>
  );
}

export default ExecutiveDashboard;
